import { type Benefit } from '@/domain/benefits/types';
import { type Product, type ProductBrief, securityLevelOptions } from '@/domain/products/types';
import parse from 'html-react-parser';
import { Tooltip } from 'primereact/tooltip';
import { Fragment } from 'react';

import ProductNewsTable from '../../../ProductNewsTable';
import ProductCard from '../../../ProductPage/ProductCard';
import { ManagersSection } from './components';
import VideoSection from './components/VideoSection';
import styles from './Overview.module.scss';

interface OverviewProps {
  product: Product;
  isInternal: boolean;
}

const renderRelatedSolutions = (relatedProducts: ProductBrief[]) => (
  <>
    <h4 className={styles.title}>Related Solutions</h4>
    <div className={styles.productList}>
      {relatedProducts.map((related) => (
        <ProductCard key={related.idProduct} product={related} layout="small" />
      ))}
    </div>
  </>
);

const renderRelatedAddons = (relatedAddons: ProductBrief[]) => (
  <>
    <h4 className={styles.title}>Add-Ons</h4>
    <div className={styles.productList}>
      {relatedAddons.map((related) => (
        <ProductCard key={related.idProduct} product={related} layout="small" />
      ))}
    </div>
  </>
);

const groupBenefits = (benefits: Benefit[]) => {
  const groups = [] as Array<{ group: string; values: Benefit[] }>;
  benefits.forEach((benefit) => {
    const index = groups.findIndex((g) => g.group.toUpperCase() === benefit.group.toUpperCase());

    if (index >= 0) {
      const valueIndex = groups[index].values.findIndex(
        (g) => g.benefit.toUpperCase() === benefit.benefit.toUpperCase(),
      );
      if (valueIndex === -1) {
        groups[index].values.push(benefit);
      }
    } else {
      groups.push({ group: benefit.group, values: [benefit] });
    }
  });

  return groups
    .map(({ group, values }) => ({
      group,
      values: [...values].sort((a, b) => (a.benefit < b.benefit ? -1 : 1)),
    }))
    .sort((a, b) => (a.group < b.group ? -1 : 1));
};

const Overview = ({ product, isInternal }: OverviewProps) => {
  const subject = `Automation Solution Finder - ${product.product}`;

  return (
    <div className={styles.mainContainer}>
      <p className={styles.description}>{product.description}</p>
      {product.descriptionForProposals && (
        <>
          <div>{parse(product.descriptionForProposals)}</div>
          <br />
          <hr />
        </>
      )}
      {product.videos?.length > 0 && <VideoSection videos={product.videos} />}

      {product.news?.length > 0 && (
        <>
          <h4 className={styles.title}>Discover More</h4>
          <ProductNewsTable news={product.news ?? []} readonly />
        </>
      )}

      {isInternal && <ManagersSection product={product} />}

      {isInternal && product.idEam != null && (
        <div className={styles.container}>
          <p className={styles.title}>
            <a
              className={styles.emailLink}
              href={`https://alm.andritz.com/jira/secure/insight/assets/EAM-${product.idEam}`}
              target="_blank"
              rel="noreferrer"
            >
              <b>LINK CMDB</b>
            </a>
          </p>
        </div>
      )}

      {product.userPersonas?.length > 0 && (
        <>
          <h4 className={styles.title}>End user</h4>
          <div className={styles.container}>
            {product.userPersonas?.map((persona) => (
              <Fragment key={persona.idUserPersona}>
                <span>• {persona.userPersona}</span>
                <br />
              </Fragment>
            ))}
          </div>
        </>
      )}
      {product.benefits?.length > 0 && (
        <>
          <h4 className={styles.title}>Benefits</h4>
          {groupBenefits(product.benefits).map(({ group, values }) => (
            <div key={group} className={styles.container}>
              <span>
                <b>{group}</b>
              </span>
              <div className={styles.container}>
                {values.map((benefit) => (
                  <Fragment key={benefit.idBenefit}>
                    <Tooltip
                      target={`[id='${benefit.idBenefit}']`}
                      mouseTrack
                      mouseTrackLeft={10}
                      content={benefit.tooltip}
                      className={styles.tooltip}
                    />
                    <span id={`${benefit.idBenefit}`}>• {benefit.benefit}</span>
                    <br />
                  </Fragment>
                ))}
              </div>
            </div>
          ))}
        </>
      )}

      {(product.capabilitySecurityLevel != null || product.cyberSecurityExpert != null) && (
        <>
          <h4 className={styles.title}>Security</h4>
          {product.capabilitySecurityLevel && (
            <div className={styles.container}>
              <p className={styles.title}>
                <b>Capability Security Level(SL-C):</b>
                <span style={{ marginRight: '0.3rem', marginLeft: '0.3rem' }}>
                  {securityLevelOptions.find((c) => c.value === product.capabilitySecurityLevel)?.label}
                </span>
                <Tooltip target=".custom-target-icon" />
                <i
                  className="custom-target-icon pi pi-info-circle p-text-secondary p-overlay-badge"
                  data-pr-tooltip={
                    securityLevelOptions.find((c) => c.value === product.capabilitySecurityLevel)?.tooltip
                  }
                  style={{ fontSize: '1rem', cursor: 'pointer' }}
                />
                <br />
              </p>
            </div>
          )}
          {product.cyberSecurityExpert && (
            <div className={styles.container}>
              <p className={styles.title}>
                <b>Cyber Security Product Expert - CSPE:</b> <span>{product.cyberSecurityExpert.name}</span>
                <span> - </span>
                <a
                  href={`mailto:${product.cyberSecurityExpert.email}?subject=${encodeURIComponent(subject)}`}
                  className={styles.emailLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  {product.cyberSecurityExpert.email}
                </a>
              </p>
            </div>
          )}
        </>
      )}

      {product.lifeCycles?.length > 0 && (
        <>
          <h4 className={styles.title}>Plant Lifecycle Management</h4>
          <div className={styles.container}>
            {product.lifeCycles?.map((lifeCycle) => (
              <Fragment key={lifeCycle.idLifeCycle}>
                <span>• {lifeCycle.lifeCycle}</span>
                <br />
              </Fragment>
            ))}
          </div>
        </>
      )}
      {product.trainings?.length > 0 && (
        <>
          <h4 className={styles.title}>Trainings</h4>
          <div className={styles.container}>
            {[...product.trainings]
              ?.sort((a, b) => a.courseOrder - b.courseOrder)
              .map((training) => (
                <Fragment key={training.idTraining}>
                  <p>
                    <b>
                      {training.courseOrder}){' '}
                      {training.link ? (
                        <a className={styles.emailLink} href={training.link} target="_blank" rel="noreferrer">
                          {training.courseName}
                        </a>
                      ) : (
                        training.courseName
                      )}
                    </b>
                    {!!training.description && `: ${training.description}`}
                  </p>
                </Fragment>
              ))}
          </div>
        </>
      )}

      <h4 className={styles.title}>Group</h4>
      <div className={styles.container}>
        <span>{product.group?.productGroup}</span>
      </div>

      {product.industries?.length > 0 && (
        <>
          <h4 className={styles.title}>Industries</h4>
          <div className={styles.container}>
            {product.industries?.map((industry) => (
              <Fragment key={industry.idIndustry}>
                <span>• {industry.industry}</span>
                {industry.industryArea ? <span>: {industry.industryArea.industryArea}</span> : null}
                <br />
              </Fragment>
            ))}
          </div>
        </>
      )}

      {isInternal && product.countries?.length > 0 && (
        <>
          <h4 className={styles.title}>Available Countries</h4>
          <div className={styles.container}>
            {product.countries?.map((country) => (
              <Fragment key={country.idCountry}>
                <span>• {country.name}</span>
                <br />
              </Fragment>
            ))}
          </div>
        </>
      )}

      {product.relatedAddons?.length ? renderRelatedAddons(product.relatedAddons) : null}
      {product.relatedProducts?.length ? renderRelatedSolutions(product.relatedProducts) : null}
    </div>
  );
};

export default Overview;
